import React, { useContext } from "react";
import {Tip, Box, ResponsiveContext, Text, Heading} from "grommet";
import {formatCurrency} from "../../../utils/numbers";
import {formatDate, formatDateUTC} from "../../../utils/dates";
import {getCSVConfig} from "../../../utils/csv";
import Badge from "../../Badge";
import { sentencecase, uncamelcase, unsnakecase } from "../../../utils/string";
import {formatName} from "../../../utils/names";
import DottedText from "../../DottedText";
import CustomerTag from "../../CustomerTag";
import InvoiceTag from "../../InvoiceTag";
import DataTableComponent from "../../DataTable";
import { useRouter } from "next/router";
import TransactionDetails from "./TransactionDetails";

const UpcomingPayoutList = ({
    data,
}) => {
    const router = useRouter();
    const { action, transactionId } = router.query;
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const reshapedData = ((data) => {
        if (!data) return null;
        return data.payments.map((transaction, index) => {
            const {
                amount, order, created_at, recipient, payout_date
            } = transaction;
            const companyName = recipient ? recipient.company_name : '-';
            const customerName = formatName(recipient, true, '-');
            return {
                ...transaction,
                amount: parseFloat(amount),
                formatted_created_at: formatDate(created_at),
                formatted_payout_date: payout_date ? formatDateUTC(payout_date) : '',
                created_at: new Date(created_at),
                company_name: companyName,
                customer_name: customerName,
                invoice_number: order?.identifier || '',
                index: index,
                customer: (recipient && recipient?.company_name) ? companyName : customerName
            };
        });
    })(data);

    const columns = isMobile ? [
        {
            selector: row => row.id,
            cell: (datum) => {
                return (
                    <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                        <Box data-tag="allowRowEvents">
                            <Text data-tag="allowRowEvents" size="small" color="text-weak">{datum?.formatted_created_at} • {sentencecase(unsnakecase(datum?.display_type))}</Text>
                            <Text data-tag="allowRowEvents">{datum?.recipient?.company_name || formatName(datum?.recipient)}</Text>
                            <Text data-tag="allowRowEvents" size="small" color="text-weak">{datum?.invoice_number}</Text>
                        </Box>
                        <Box data-tag="allowRowEvents">
                            <Text data-tag="allowRowEvents" wordBreak="keep-all">{formatCurrency(datum?.amount, datum?.currency)}</Text>
                        </Box>
                    </Box>
                )
            }
        }
    ] : [
        {
            selector: row => row.created_at,
            name: 'Date',
            sortField: 'created_at',
            grow: 1.5,
            cell: (datum) => datum.formatted_created_at,
        },
        {
            selector: row => row.display_type,
            name: 'Type',
            grow: 1.2,
            cell: (datum) => (
                <Box direction="row" gap="xsmall">
                    {uncamelcase(unsnakecase(datum.display_type))}
                </Box>
            )
        },
        {
            selector: row => row.amount,
            name: 'Amount',
            right: true,
            type: "currency",
            sortField: 'amount',
            cell: (datum) => formatCurrency(datum.amount, datum.currency)
        },
        {
            selector: row => row.customer_name,
            name: 'Customer',
            grow: 2,
            cell: (datum) => datum?.recipient?.id && (<CustomerTag id={datum.recipient.id} inline />)
        },
        {
            selector: row => row.invoice_number,
            name: 'Invoice',
            sortable: false,
            grow: 2,
            cell: (datum) => {
                if (!datum.referencable_id) return '';
                if (datum.identifiers && datum.identifiers.length > 1) {
                    return (
                        <Tip content={(
                            <Box pad="small" width={{max: 'medium'}}>
                                {(datum.identifiers).map(identifier => (
                                    <Box key={identifier} margin="xxsmall">
                                        <Badge>{identifier}</Badge>
                                    </Box>
                                ))}
                            </Box>
                        )}>
                            <DottedText size="small">
                                {datum.identifiers.length} invoices
                            </DottedText>
                        </Tip>)
                }
                if (datum.referencable_id && datum.type !== 'Invoice') return <InvoiceTag id={datum.referencable_id} inline />;
            },
        },
    ].filter(Boolean);

    return (
        <>
            <DataTableComponent
                data={reshapedData}
                columns={columns}
                isServer={false}
                noTableHead
                controls={(
                    <Heading
                        responsive={false}
                        align="start"
                        level={2}
                        size="medium"
                        margin={{horizontal: 'xsmall', vertical: 'small'}}>
                        Upcoming
                    </Heading>
                )}
                titleProps={{margin: {top: '0'}}}
                dataTableWrapperProps={{margin: "0"}}
                exportConfigs={getCSVConfig(columns)}
                onRowClicked={row => row?.uuid && router.push(
                    `/reports?action=view&transactionId=${row.id}`,
                    undefined, {
                        scroll: false
                    }
                )}
                dataName="pending_payout_details" />
            { action && action === "view" && transactionId && (
                <TransactionDetails transactionId={transactionId} onClose={() => router.back()} />
            )}
        </>
    );
};

export default UpcomingPayoutList;
